import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  CodeBuilderApi,
  SendInterviewMessageRequest,
} from './CodeBuilderApi';
import type {
  CodeInterviewResponse,
  IssueGetResult,
  IssuesListResult,
} from './types';

export class CodeBuilderClient implements CodeBuilderApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async sendInterviewMessage(
    request: SendInterviewMessageRequest,
    signal?: AbortSignal,
  ): Promise<CodeInterviewResponse> {
    return this.request('/interview', {
      method: 'POST',
      body: JSON.stringify(request),
      signal,
    });
  }

  async listIssues(repoEntityRef: string): Promise<IssuesListResult> {
    return this.request(
      `/issues?repoEntityRef=${encodeURIComponent(repoEntityRef)}`,
    );
  }

  async getIssue(
    repoEntityRef: string,
    number: number,
  ): Promise<IssueGetResult> {
    return this.request(
      `/issues/${number}?repoEntityRef=${encodeURIComponent(repoEntityRef)}`,
    );
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const discoveredBaseUrl = await this.discoveryApi.getBaseUrl('code-builder');
    const baseUrl =
      discoveredBaseUrl && discoveredBaseUrl !== '/'
        ? discoveredBaseUrl
        : '/api/code-builder';

    const headers = new Headers(init.headers);
    if (init.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers,
    });

    if (!response.ok) {
      const message = await readErrorMessage(response);
      throw new Error(
        message || `Code Builder request failed: ${response.status}`,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body?.error?.message ?? body?.message ?? '';
  } catch {
    return '';
  }
}
